/**
 * PendingEmailDialog — Dialog modal qui affiche le PendingEmailEditor.
 *
 * Utilise depuis BriefActions sur le dashboard : permet de valider un mail
 * Juliette sans quitter la page. Le caller controle ouverture/fermeture via
 * `open` + `onOpenChange`.
 *
 * Etats supportes :
 *  - `pendingId === null` : on attend la fin de la generation cote serveur.
 *    Affiche un spinner + texte "L'IA redige le mail…" pour rassurer l'agent.
 *  - `pendingId` defini : monte PendingEmailEditor avec le brouillon.
 */

import { Loader2, Sparkles } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { rejectAbandonedDraft } from "@/lib/reject-pending-draft";
import { PendingEmailEditor } from "./PendingEmailEditor";

export interface PendingEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Id du pending_email a afficher. Si null, on est encore en generation
   *  et on affiche le loading state au lieu de l'editor. */
  pendingId: string | null;
  /** Nom du contact (affiche dans le titre, optionnel — sinon "Mail à valider"). */
  contactName?: string;
}

export function PendingEmailDialog({
  open,
  onOpenChange,
  pendingId,
  contactName,
}: PendingEmailDialogProps) {
  const queryClient = useQueryClient();

  // Fermeture déclenchée par l'utilisateur (croix, Échap, clic dehors) : si un
  // brouillon a été généré mais jamais envoyé/annulé, on l'auto-annule pour ne
  // pas consommer le quota anti-spam (no-op si déjà finalisé, cf. helper).
  // L'envoi/rejet explicite passe par `onDone` → prop brut `onOpenChange`, donc
  // ce nettoyage ne se déclenche que sur un vrai abandon.
  const handleOpenChange = (next: boolean) => {
    if (!next && pendingId) {
      void rejectAbandonedDraft("pending_emails", pendingId).then(() => {
        void queryClient.invalidateQueries({ queryKey: ["pending-emails"] });
      });
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span>
              {contactName ? `Mail pour ${contactName}` : "Mail à valider"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-2xs">
            {pendingId
              ? "Brouillon rédigé par Juliette — relis, ajuste si besoin, puis envoie."
              : "Juliette analyse le contexte du contact et rédige un mail calibré."}
          </DialogDescription>
        </DialogHeader>

        {pendingId ? (
          <PendingEmailEditor
            pendingId={pendingId}
            hideHeader
            onDone={() => onOpenChange(false)}
          />
        ) : (
          <GeneratingState />
        )}
      </DialogContent>
    </Dialog>
  );
}

function GeneratingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 gap-4">
      <div className="relative">
        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
          <Sparkles className="h-7 w-7 text-white animate-pulse" />
        </div>
        <Loader2 className="absolute -bottom-1 -right-1 h-6 w-6 text-indigo-500 animate-spin" />
      </div>
      <div className="text-center space-y-1">
        <p className="text-sm font-medium text-foreground">L'IA est en train de rédiger le mail</p>
        <p className="text-xs text-muted-foreground">
          Juliette analyse le contexte du contact, son profil DISC et l'historique d'échanges.
          <br />
          Ça prend généralement 5 à 10 secondes.
        </p>
      </div>
    </div>
  );
}
